import { Trans, withI18n } from '@lingui/react'
import classNames from 'clsx'
import { bool, number, oneOf, shape } from 'prop-types'
import { compose } from 'redux'

import {
  AVAILABLE_PRESCRIPTION_TYPES,
  AVAILABLE_PRODUCT_TEMPLATES,
  PRESCRIPTION_TYPE_FREE,
  PRESCRIPTION_TYPE_INSURANCE,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRESCRIPTION_TYPE_PRIVAT,
  PRESCRIPTION_TYPE_TO_PRICE_TYPE,
} from 'shared/consts/productTemplates'
import { TENANT_COM } from 'shared/consts/tenants'
import {
  RELEASE_TOGGLE_VARIANT_ATTTRIBUTES_ENABLED,
  VARIANT_ATTRIBUTES,
} from 'shared/experiments/consts'
import {
  isExperimentVariableTrue,
  isSaeOnly,
} from 'shared/experiments/utils/featureFlags'
import { isCdnUrlHook } from 'shared/utils/imageUtils'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import Button from 'views/components/atoms/Button'
import CdnImage from 'views/components/atoms/CdnImage'
import IconCaretUp from 'views/components/atoms/Icon/IconCaretUp'
import IconCart from 'views/components/atoms/Icon/IconCart'
import Image from 'views/components/atoms/Image'
import ButtonWithIcon from 'views/components/molecules/ButtonWithIcon'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import { shouldUseOTCMode } from 'views/components/organisms/ProductVariants/helpers'
import { RetailPrice } from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantComponents'
import { RX_SELECT_MODAL_ID } from 'views/consts'
import {
  MAX_PRODUCT_IMAGE_SIZE,
  MIN_PRODUCT_IMAGE_SIZE,
  PDP_MAIN_IMAGE_SIZES,
} from 'views/consts/product-image-sizes'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  isProductAvailable,
  isProductOutOfStock,
  isProductOutOfTrade,
} from 'views/utils/productUtils'
import { i18nType, mountPointsType, ProductType } from 'views/utils/propTypes'

import './BuyBoxSticky.scss'

/* eslint-disable react/prop-types */
const PriceLabel = ({
  useOTCMode,
  prescriptionType,
  isVariantAttributeFlag,
  superScripts: { price: priceSS },
  i18n,
}) => {
  const title =
    useOTCMode || prescriptionType === PRESCRIPTION_TYPE_PRIVAT
      ? i18n._('general.label.yourPrice')
      : i18n._('general.label.additionalCharge')
  const priceSuperScripts = priceSS && <sup>{priceSS}</sup>

  const priceLabelStyle = classNames('o-BuyBoxSticky__price-label', {
    'a-h6 u-color--grey u-font-weight--normal': isVariantAttributeFlag,
  })

  return (
    <span className={priceLabelStyle}>
      {title}
      {priceSuperScripts}
    </span>
  )
}
/* eslint-enable react/prop-types */

const BuyBoxSticky = ({
  isMasterPage,
  lowestPrice,
  product: {
    id,
    name,
    offerId,
    thumbnailURL,
    unitPricing,
    unit,
    unitQuantityString,
    stockStatus,
    prices,
    subtitle,
  },
  prescriptionType,
  productTemplate,
  publicRuntimeConfig: {
    publicConfig: { superScripts, tenant, currency },
    pageProperties: { experiments = [] },
  },
  i18n,
  mountPoints,
  disableAddToCartButton,
}) => {
  const isAvailable = isProductAvailable(stockStatus)
  const isOutOfStock = isProductOutOfStock(stockStatus)
  const isOutOfTrade = isProductOutOfTrade(stockStatus)

  if (!isAvailable || isOutOfStock || isOutOfTrade) {
    return null
  }

  const isVariantAttributeFlag = isExperimentVariableTrue(
    VARIANT_ATTRIBUTES,
    RELEASE_TOGGLE_VARIANT_ATTTRIBUTES_ENABLED,
    experiments
  )

  const realPriceAmount = getPriceForType(
    prices,
    PRESCRIPTION_TYPE_TO_PRICE_TYPE[prescriptionType]
  )
  const retailPriceProps = {
    amount: realPriceAmount.amount,
    prescriptionType,
    isMasterPage,
    lowestPrice,
    currency,
  }
  const useOTCMode = shouldUseOTCMode({ prescriptionType, productTemplate })
  const retailPriceClassName = classNames(
    'u-font-weight--bold u-padding--right o-BuyBoxSticky__quantity-wrapper',
    {
      'u-color--red': useOTCMode,
      'o-BuyBoxSticky__price-label-width': isVariantAttributeFlag,
    }
  )

  const retailPriceStyle = classNames('a-h2 u-no-margin u-no-wrap', {
    'a-h4 u-font-weight--bold': isVariantAttributeFlag,
  })
  const imageStyle = classNames('max100width-image o-BuyBoxSticky__image', {
    'o-BuyBoxSticky-imageMinWidth': isVariantAttributeFlag,
  })

  const isReleaseSwitchEnabled = isSaeOnly(experiments, tenant)

  const additionalParams = isReleaseSwitchEnabled ? { offer: offerId } : {}

  const queryParams = {
    eventType: 'conversion',
    eventName: 'addToCart_sticky_buy_box',
    cartAction: 'add',
    product: id,
    prescriptionType,
    quantity: 1,
    ...additionalParams,
  }

  const href =
    tenant === TENANT_COM &&
    isReleaseSwitchEnabled &&
    [PRESCRIPTION_TYPE_INSURANCE, PRESCRIPTION_TYPE_FREE].includes(
      prescriptionType
    )
      ? `#${RX_SELECT_MODAL_ID}`
      : mountPoints.getAddToCart(queryParams)

  return (
    <div
      className="o-BuyBoxSticky u-background-color--background u-position--fixed u-hidden@medium"
      data-clientside-hook="buy-box-sticky"
    >
      <div className="a-box l-flex l-flex--distribute-space o-BuyBoxSticky__container">
        <div className="l-flex o-BuyBoxSticky__wrapper-product">
          <div className="u-visible@tiny u-padding--right o-BuyBoxSticky__image-wrapper">
            {isCdnUrlHook(thumbnailURL) ? (
              <CdnImage
                fileName={thumbnailURL}
                sizes={PDP_MAIN_IMAGE_SIZES}
                minWidth={MIN_PRODUCT_IMAGE_SIZE}
                maxWidth={MAX_PRODUCT_IMAGE_SIZE}
                maxHeight={MAX_PRODUCT_IMAGE_SIZE}
                alt={name}
                imgClassName="o-BuyBoxSticky__image"
              />
            ) : (
              <Image src={thumbnailURL} alt={name} className={imageStyle} />
            )}
          </div>

          <div className="o-BuyBoxSticky__info u-padding--right">
            {isVariantAttributeFlag ? (
              <div className="a-p u-no-margin o-BuyBoxSticky__quantity u-color--grey subtitle__twoLinesMax">
                {subtitle}
              </div>
            ) : (
              <>
                <div className="a-h2 u-no-margin u-font-weight--bold u-no-wrap o-BuyBoxSticky__quantity">
                  {unitQuantityString} {unit}
                </div>
                <div className="a-h6 u-no-margin u-font-weight--normal u-no-wrap">
                  {unitPricing}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="l-flex o-BuyBoxSticky__wrapper-price">
          <div className={retailPriceClassName}>
            <PriceLabel
              isVariantAttributeFlag={isVariantAttributeFlag}
              prescriptionType={prescriptionType}
              superScripts={superScripts}
              useOTCMode={useOTCMode}
              i18n={i18n}
            />
            <div className={retailPriceStyle}>
              <RetailPrice {...retailPriceProps} />
            </div>
          </div>
          <ButtonWithIcon
            name={`sticky-buy-box-${id}`}
            href={href}
            icon={<IconCart />}
            data-clientside-hook="chargeUrlSearchParams"
            disabled={disableAddToCartButton}
          >
            <Trans id="general.button.label.addToCart" />
          </ButtonWithIcon>
        </div>
        <div className="l-flex__primary o-BuyBoxSticky__buttonScroller">
          <Button
            style="buttonPrimary"
            id="selectProductVariantButtonDrawer"
            className="a-h3 u-font-weight--normal u-no-margin--bottom"
          >
            <TextWithIcon icon={<IconCaretUp size="small" />}></TextWithIcon>
            <Trans id="variantAttributes.message.selectProductVariant" />
          </Button>
        </div>
      </div>
    </div>
  )
}

BuyBoxSticky.propTypes = {
  product: shape(ProductType).isRequired,
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES).isRequired,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  publicRuntimeConfig: shape({}),
  i18n: i18nType.isRequired,
  mountPoints: mountPointsType.isRequired,
  isMasterPage: bool,
  lowestPrice: number,
  disableAddToCartButton: bool,
}

BuyBoxSticky.defaultProps = {
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  publicRuntimeConfig: {},
  isMasterPage: false,
  disableAddToCartButton: false,
}

export default compose(
  withI18n(),
  withShopAndRuntimeConfig,
  withMountPoints
)(BuyBoxSticky)
